import { useState, useEffect, useCallback, useRef } from 'react';
import { FileStat, FolderSizeResult } from '../api/IFileSystem';
import { useFileManagerStore } from '../store/useFileManagerStore';
import { useFileSearchStatusStore } from '../store/useFileSearchStatus';
import { FileSearchStatus } from '../../../types';

interface FolderSizeCache {
  [path: string]: {
    result: FolderSizeResult;
    timestamp: number;
  };
}

// Type guard for WebRTCSystem-like object with folder size methods
interface FolderSizeCapable {
  getFolderSize: (path: string, frn?: number, volume?: string) => Promise<FolderSizeResult | null>;
  getFolderSizes: (folders: Array<{ path: string; frn?: number; volume?: string }>) => Promise<Record<string, FolderSizeResult>>;
  getVolumeSize: (volume: string) => Promise<{ total_size: number; free_size: number; used_size: number } | null>;
}

function hasFolderSizeMethods(fs: any): fs is FolderSizeCapable {
  return fs && typeof fs.getFolderSize === 'function' && typeof fs.getFolderSizes === 'function' && typeof fs.getVolumeSize === 'function';
}

const CACHE_TTL = 300000; // 5 minutes cache (increased from 1 minute)
const MAX_BATCH = 200; // Increased from 40 to 200 for better performance
const CURRENT_FOLDER_DELAY_MS = 0; // Removed delay: fetch immediately

/**
 * Hook for fetching folder sizes via WebRTC DataChannel
 *
 * IMPORTANT: Folder sizes are fetched AFTER file list is loaded.
 * This is a lazy-loading pattern - sizes are only fetched for visible folders.
 */
export function useFolderSize(currentPath: string, files: FileStat[]) {
  const [sizes, setSizes] = useState<Record<string, FolderSizeResult>>({});
  const [currentFolderSize, setCurrentFolderSize] = useState<FolderSizeResult | null>(null);
  const [loading, setLoading] = useState(false);
  const cacheRef = useRef<FolderSizeCache>({});
  const pendingRef = useRef<Set<string>>(new Set());
  const inFlightRef = useRef(false);
  const currentTimerRef = useRef<number | null>(null);
  const { fs } = useFileManagerStore();
  const searchEngineStatus = useFileSearchStatusStore((s) => s.status);
  const isSearchEngineReady = searchEngineStatus === FileSearchStatus.READY;

  // Get current folder size (only once per path change)
  useEffect(() => {
    if (!currentPath || currentPath.length < 2) return;
    if (!hasFolderSizeMethods(fs)) return;
    // Don't fetch folder sizes if search engine is not ready
    if (!isSearchEngineReady) return;

    const isRoot = currentPath === 'root';
    if (isRoot) {
      setCurrentFolderSize(null);
      return;
    }

    // Check if this is a drive root (e.g. "C:" or "C:\")
    // For drive root, we'll calculate the sum of all first-level items' sizes
    // This is done in a separate useEffect that watches the sizes state
    const isDriveRoot = /^[a-zA-Z]:[\\/]?$/.test(currentPath);
    if (isDriveRoot) {
      // Don't set currentFolderSize here - it will be calculated based on first-level items
      // Set to null initially, will be updated when folder sizes are fetched
      setCurrentFolderSize(null);
      return;
    }

    const cached = cacheRef.current[currentPath];
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      setCurrentFolderSize(cached.result);
      return;
    }

    // Fetch current folder size
    if (currentTimerRef.current) {
      window.clearTimeout(currentTimerRef.current);
    }
    currentTimerRef.current = window.setTimeout(() => {
      fs.getFolderSize(currentPath)
        .then((result) => {
          if (result) {
            cacheRef.current[currentPath] = { result, timestamp: Date.now() };
            setCurrentFolderSize(result);
          }
        })
        .catch((e) => {
          console.error('[useFolderSize] Failed to get current folder size:', e);
          setCurrentFolderSize(null);
        });
    }, CURRENT_FOLDER_DELAY_MS);
    return () => {
      if (currentTimerRef.current) {
        window.clearTimeout(currentTimerRef.current);
        currentTimerRef.current = null;
      }
    };
  }, [currentPath, fs, isSearchEngineReady]);

  // For drive root: calculate sum of all first-level items' sizes
  useEffect(() => {
    const isDriveRoot = /^[a-zA-Z]:[\\/]?$/.test(currentPath);
    if (!isDriveRoot) return;

    // Calculate total size of all first-level items
    let totalSize = 0;
    let fileCount = 0;
    let dirCount = 0;
    let allFoldersFetched = true;

    for (const file of files) {
      if (file.type === 'file') {
        totalSize += file.size;
        fileCount++;
      } else if (file.type === 'dir') {
        const folderSize = sizes[file.path];
        if (folderSize) {
          totalSize += folderSize.total_size;
          fileCount += folderSize.file_count;
          dirCount += folderSize.dir_count + 1; // +1 for the folder itself
        } else {
          // Folder size not yet fetched
          allFoldersFetched = false;
        }
      }
    }

    // Only update if we have at least some data (partial update is OK)
    if (totalSize > 0) {
      const result: FolderSizeResult = {
        total_size: totalSize,
        allocated_size: totalSize,
        file_count: fileCount,
        dir_count: dirCount,
        percent: 0, // Not meaningful for root
      };
      setCurrentFolderSize(result);

      if (!allFoldersFetched) {
      } else {
      }
    }
  }, [currentPath, files, sizes]);

  // Batch fetch folder sizes for visible folders (lazy loading)
  // Optimized: Allow concurrent requests by queuing instead of blocking
  const fetchFolderSizes = useCallback(async (visibleFolders: FileStat[]) => {
    if (!hasFolderSizeMethods(fs)) return;
    // Don't fetch folder sizes if search engine is not ready
    if (!isSearchEngineReady) return;

    // Filter to only directories not already cached or pending
    const dirsToFetch = visibleFolders.filter(f =>
      f.type === 'dir' &&
      !cacheRef.current[f.path] &&
      !pendingRef.current.has(f.path)
    );

    // Apply cached values first
    const cached: Record<string, FolderSizeResult> = {};
    visibleFolders.forEach(f => {
      if (f.type === 'dir' && cacheRef.current[f.path]) {
        cached[f.path] = cacheRef.current[f.path].result;
      }
    });
    if (Object.keys(cached).length > 0) {
      setSizes(prev => ({ ...prev, ...cached }));
    }

    if (dirsToFetch.length === 0) {
      return;
    }

    // Process all folders in batches concurrently (removed inFlightRef blocking)
    const batches: FileStat[][] = [];
    for (let i = 0; i < dirsToFetch.length; i += MAX_BATCH) {
      batches.push(dirsToFetch.slice(i, i + MAX_BATCH));
    }

    // Mark all as pending
    dirsToFetch.forEach(f => pendingRef.current.add(f.path));

    setLoading(true);

    // Process batches in parallel (but limit concurrency to avoid overwhelming)
    const processBatch = async (batch: FileStat[]) => {
      try {
        // Note: FRN is no longer passed from frontend (removed from ListDir for performance)
        // Backend will fetch FRN on-demand using GetFileReferenceNumber(path)
        const results = await fs.getFolderSizes(
          batch.map(f => ({ path: f.path }))
        );


        // Update cache
        Object.entries(results).forEach(([path, result]) => {
          cacheRef.current[path] = {
            result,
            timestamp: Date.now()
          };
          pendingRef.current.delete(path);
        });

        setSizes(prev => ({ ...prev, ...results }));
      } catch (e) {
        console.error('[useFolderSize] Failed to fetch folder sizes:', e);
        // Clear pending on error
        batch.forEach(f => pendingRef.current.delete(f.path));
      }
    };

    // Process first 2 batches in parallel, then queue the rest
    // This prevents overwhelming the DataChannel
    const MAX_CONCURRENT_BATCHES = 2;
    for (let i = 0; i < batches.length; i += MAX_CONCURRENT_BATCHES) {
      const concurrentBatches = batches.slice(i, i + MAX_CONCURRENT_BATCHES);
      await Promise.all(concurrentBatches.map(processBatch));
    }

    setLoading(false);
  }, [fs, isSearchEngineReady]);

  // Smart cache cleanup: keep parent directory info when navigating
  useEffect(() => {
    // Only clear sizes for the current path's subfolders
    // Keep cache for parent directories (useful when navigating back)
    setSizes(prev => {
      const newSizes: Record<string, FolderSizeResult> = {};
      // Keep cached sizes that might still be relevant
      Object.entries(prev).forEach(([path, size]) => {
        // Keep if path is ancestor or sibling of current path
        if (currentPath.startsWith(path) || path.startsWith(currentPath.slice(0, currentPath.lastIndexOf('\\')))) {
          newSizes[path] = size;
        }
      });
      return newSizes;
    });

    // Clear pending requests (they're for old path)
    pendingRef.current.clear();
    // Note: cacheRef is NOT cleared - it persists across navigation for better performance
  }, [currentPath]);

  return {
    sizes,              // Subfolder sizes map
    currentFolderSize,  // Current folder size
    loading,
    fetchFolderSizes,   // Call this with visible folders after list renders
  };
}
